import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Wraps the app to auto-exchange a Swami Hub ?sso_token=... URL param for a local session cookie.
 * If no sso_token is present, renders children as-is.
 *
 * CRITICAL order of operations — prevents "another user's data" on SSO failure:
 *   1. Hit /api/auth/logout FIRST → server deletes the stale httpOnly cookie
 *   2. Wipe client-side storage (localStorage, sessionStorage, non-httpOnly cookie)
 *   3. THEN call /api/swami-sso/exchange with the Hub token
 *   4. VERIFY the cookie actually stuck by calling /api/auth/me
 *   5. On success → redirect to /setup or /dashboard
 *   6. On verification failure → show a clear retry UI (do NOT silently bounce to /login)
 *
 * Guarded against React.StrictMode double-mount: module-level consumedTokens Set + inFlight
 * promise ensure the one-time-use Hub token is only consumed ONCE per pageload.
 */
const consumedTokens = new Set();
let inFlight = null;

export default function SwamiSSOShell({ children }) {
  const [checking, setChecking] = useState(() => {
    try {
      return new URL(window.location.href).searchParams.has('sso_token');
    } catch {
      return false;
    }
  });
  const [errMsg, setErrMsg] = useState('');

  useEffect(() => {
    const url = new URL(window.location.href);
    const ssoToken = url.searchParams.get('sso_token');
    if (!ssoToken) return;

    if (consumedTokens.has(ssoToken) && !inFlight) {
      return;
    }

    if (!inFlight) {
      consumedTokens.add(ssoToken);
      inFlight = (async () => {
        // ── STEP 1: Force-logout any prior session (deletes httpOnly cookie server-side) ──
        try {
          await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
        } catch { /* logout always succeeds but ignore any 4xx for safety */ }

        // ── STEP 2: Wipe client-side storage and any non-httpOnly cookie copy ──
        try {
          localStorage.removeItem('access_token');
          sessionStorage.clear();
          document.cookie = 'access_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
        } catch { /* ignore */ }

        // ── STEP 3: Exchange the Hub token for a fresh session ──
        let exchangeData = null;
        try {
          const { data } = await axios.post(
            `${API_URL}/api/swami-sso/exchange`,
            { sso_token: ssoToken },
            { withCredentials: true, timeout: 12000 }
          );
          exchangeData = data;
        } catch (e) {
          const detail = e?.response?.data?.detail || e?.message || 'Network error';
          return { ok: false, stage: 'exchange', error: detail };
        }

        // ── STEP 4: VERIFY the cookie actually stuck. This catches the case where the
        //          browser silently refused to store the cookie (samesite/secure/CORS).
        try {
          const { data: me } = await axios.get(
            `${API_URL}/api/auth/me`,
            { withCredentials: true, timeout: 8000 }
          );
          if (!me || !me.id) {
            return { ok: false, stage: 'verify', error: 'Session not established' };
          }
          return { ok: true, data: exchangeData, me };
        } catch (e) {
          const detail = e?.response?.data?.detail || e?.message || 'Verification failed';
          return { ok: false, stage: 'verify', error: detail };
        }
      })();
    }

    (async () => {
      const result = await inFlight;
      url.searchParams.delete('sso_token');

      if (result.ok) {
        const needsSetup = !!result.data?.needs_setup;
        const cleanPath = needsSetup
          ? '/setup'
          : (url.pathname === '/' || url.pathname === '/login' ? '/dashboard' : url.pathname);
        const cleanUrl = cleanPath + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
        window.location.replace(cleanUrl);
      } else {
        // SSO failed at exchange OR cookie-verification stage. DO NOT bounce silently —
        // show a clear retry screen so the user isn't dumped on /login wondering what happened.
        console.error('Swami SSO failed', result);
        const friendly = result.stage === 'verify'
          ? 'Signed in, but your browser refused the session cookie. Please ensure third-party cookies are allowed for swami-oee.in, then click Retry.'
          : 'Could not validate your Swami Hub session. ' + String(result.error || '').slice(0, 180);
        setErrMsg(friendly);
        setChecking(false);
        // Make a fresh attempt possible by clearing the in-flight latch
        inFlight = null;
        consumedTokens.delete(ssoToken);
      }
    })();
  }, []);

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="swami-sso-loading">
        <div className="text-center">
          <img src="/swami-icon.png" alt="Swami" className="h-14 w-14 mx-auto rounded-lg animate-pulse" />
          <p className="mt-3 text-slate-600 text-sm font-medium">Signing you in via Swami Hub…</p>
        </div>
      </div>
    );
  }

  if (errMsg) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="swami-sso-error">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-slate-200 p-6 text-center">
          <img src="/swami-icon.png" alt="Swami" className="h-14 w-14 mx-auto rounded-lg" />
          <h2 className="mt-4 text-lg font-black text-slate-800">Couldn't complete Swami sign-in</h2>
          <p className="mt-2 text-sm text-slate-600">{errMsg}</p>
          <div className="mt-5 flex gap-2 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold"
              data-testid="sso-retry-btn"
            >
              Retry
            </button>
            <button
              onClick={() => { window.location.href = '/login'; }}
              className="px-4 py-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold"
              data-testid="sso-go-login-btn"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return children;
}
